import { atom, useStore } from "jotai";
import React from "react";
import { createPortal } from "react-dom";
import { captureException } from "utils/errorReporting";
import { SpiderSilhouette } from "./Silhouettes";

export const repeatEveryMs = 60_000;
// The nav bars remount on every route change; the schedule lives in the store so navigating doesn't reset it.
const nextCrawlAtAtom = atom(0);

type Pose = { x: number; y: number; r: number };
type Step = { to: Pose; ms: number } | { pause: number };

export const hidden: Pose = { x: 0, y: -30, r: 180 };
const toTransform = ({ x, y, r }: Pose) => `translate(${x}px, ${y}px) rotate(${r}deg)`;
const rand = (min: number, max: number) => min + Math.random() * (max - min);
const wait = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * A fresh, jittery route each load: out from behind the bar, scurry left in uneven bursts with pauses, turn,
 * scurry back, face up and climb back behind the bar. Headings: 180 down, 270 left, 90 right, 0 up.
 */
export function crawlRoute(): Array<Step> {
  const steps: Array<Step> = [];
  let pose = hidden;
  const move = (to: Partial<Pose>, ms: number) => {
    pose = { ...pose, ...to };
    steps.push({ to: pose, ms });
  };
  const pause = (min: number, max: number) => steps.push({ pause: rand(min, max) });

  move({ y: 2 }, 700);
  pause(300, 700);
  move({ r: 270 + rand(-15, 15) }, 220);
  while (pose.x > -150) {
    move({ x: pose.x - rand(14, 45), y: rand(0, 6), r: 270 + rand(-20, 20) }, rand(160, 420));
    if (Math.random() < 0.6) pause(120, 800);
  }
  pause(300, 600);
  move({ r: 90 + rand(-15, 15) }, 350);
  while (pose.x < -10) {
    move({ x: Math.min(0, pose.x + rand(18, 50)), y: rand(0, 6), r: 90 + rand(-20, 20) }, rand(160, 380));
    if (Math.random() < 0.5) pause(100, 600);
  }
  move({ x: 0, r: 0 }, 300);
  pause(200, 400);
  move({ y: hidden.y }, 600);
  return steps;
}

type CrawlLoopDeps = {
  crawl: () => Promise<void>;
  getNextAt: () => number;
  setNextAt: (at: number) => void;
  wait: (ms: number) => Promise<unknown>;
  now: () => number;
  cancelled: () => boolean;
};

/** Crawl a second after mount (or when the stored slot comes due), then a minute after each crawl ends. */
export async function runCrawlLoop({ crawl, getNextAt, setNextAt, wait, now, cancelled }: CrawlLoopDeps) {
  while (!cancelled()) {
    await wait(Math.max(1000, getNextAt() - now())); // NOSONAR S9382: crawls are spaced in sequence by design
    if (cancelled()) return;
    // Claim the slot up front: a route change mid-crawl remounts the bar and must not replay the crawl.
    setNextAt(now() + repeatEveryMs);
    await crawl(); // NOSONAR S9382: the next slot is set from when this crawl ends
    setNextAt(now() + repeatEveryMs);
  }
}

type HeaderCrawlerProps = {
  /** Smaller, with a light halo: the strip under the mobile bar is narrow and can be black. */
  mobile?: boolean;
};

/**
 * A spider that crawls along the underside of the nav bar's top-right corner a second after load and then once a
 * minute, each time on a fresh route, climbing back up out of sight in between.
 * Portaled so the bar's `overflow-hidden` can't clip it; it starts and ends tucked behind the bar (z-40 under z-50).
 */
export const HeaderCrawler = ({ mobile = false }: HeaderCrawlerProps) => {
  const store = useStore();
  const [still, setStill] = React.useState(true);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const element = ref.current;
    if (!element || globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let cancelled = false;
    let current: Animation | undefined;
    const crawl = async () => {
      let pose = hidden;
      for (const step of crawlRoute()) {
        if (cancelled) return;
        if ("pause" in step) {
          setStill(true);
          await wait(step.pause); // NOSONAR S9382: the walk's steps must play one after another
          continue;
        }
        setStill(false);
        current = element.animate([{ transform: toTransform(pose) }, { transform: toTransform(step.to) }], {
          duration: step.ms,
          easing: "ease-in-out",
          fill: "forwards",
        });
        await current.finished; // NOSONAR S9382: the walk's steps must play one after another
        // Keep the end pose inline and drop the animation, so an all-day tab doesn't pile up filled animations.
        current.commitStyles();
        current.cancel();
        pose = step.to;
      }
      setStill(true);
    };
    runCrawlLoop({
      crawl,
      getNextAt: () => store.get(nextCrawlAtAtom),
      setNextAt: (at) => store.set(nextCrawlAtAtom, at),
      wait,
      now: Date.now,
      cancelled: () => cancelled,
    }).catch((error: unknown) => {
      // `finished` rejects with an AbortError when the cleanup cancels a move mid-step; that's the intended stop.
      if (error instanceof DOMException && error.name === "AbortError") return;
      captureException(error);
    });
    return () => {
      cancelled = true;
      current?.cancel();
    };
  }, [store]);

  return createPortal(
    <div
      ref={ref}
      aria-hidden="true"
      data-still={still || undefined}
      style={{ transform: toTransform(hidden) }}
      className="group pointer-events-none fixed top-nav-top right-8 z-40 -mt-1 text-black motion-reduce:hidden"
    >
      {mobile ? <SpiderSilhouette width={26} height={21} outlined /> : <SpiderSilhouette width={34} height={27} />}
    </div>,
    document.body,
  );
};
